import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Heart, Copy, Check, Sparkles, Smartphone, ArrowRight } from 'lucide-react';
import { trackDownloadClick } from '../lib/track';

const IOS_LINK = 'https://apps.apple.com/us/app/couplein/id6749887412';
const ANDROID_LINK = 'https://play.google.com/store/apps/details?id=dev.expo.couplein&pcampaignid=web_share';

export default function ConnectLanding() {
  const [searchParams] = useSearchParams();
  const routeParams = useParams();
  const navigate = useNavigate();

  const [copied, setCopied] = useState(false);
  const [platform, setPlatform] = useState('unknown'); // 'ios' | 'android' | 'desktop'

  // Extract code from query parameter (?code=... or ?c=...) or route parameter (/connect/:code)
  const rawCode = searchParams.get('code') || searchParams.get('c') || routeParams.code || '';
  const code = (rawCode.match(/\d{6}/) ? rawCode.match(/\d{6}/)[0] : rawCode.replace(/\D/g, '').slice(0, 6)) || '';

  useEffect(() => {
    // Detect mobile platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroidDevice = /android/i.test(userAgent);

    if (isIOSDevice) {
      setPlatform('ios');
    } else if (isAndroidDevice) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // If code exists, save to localStorage so it's retained during login & onboarding
    if (code && code.length === 6) {
      try {
        localStorage.setItem('pending_partner_code', code);
      } catch (e) {
        console.warn('Could not access localStorage', e);
      }

      // Auto-trigger deep link on mobile devices
      if (isIOSDevice || isAndroidDevice) {
        tryDeepLink(code, isAndroidDevice);
      }
    }
  }, [code]);

  const tryDeepLink = (inviteCode, isAndroid = false) => {
    if (!inviteCode) return;
    const deepLinkUrl = `couplein://connect?code=${encodeURIComponent(inviteCode)}`;

    if (isAndroid) {
      window.location.href = deepLinkUrl;
    } else {
      window.location.href = deepLinkUrl;
    }
  };

  const handleOpenApp = () => {
    if (platform === 'desktop') {
      // Desktop computer: redirect to iOS App Store page
      window.open(IOS_LINK, '_blank', 'noopener,noreferrer');
      toast.info('Opening CoupleIn on App Store…');
      return;
    }

    if (code) {
      tryDeepLink(code, platform === 'android');
      toast.info('Opening CoupleIn app…');
    } else {
      window.location.href = 'couplein://';
      toast.info('Opening CoupleIn app…');
    }
  };

  const handleCopyCode = async () => {
    if (!code) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const input = document.createElement('input');
        input.value = code;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      setCopied(true);
      toast.success('Code copied to clipboard! 💕');
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      toast.error('Failed to copy code');
    }
  };

  const handleContinueWeb = () => {
    if (code) {
      localStorage.setItem('pending_partner_code', code);
      navigate(`/connection?code=${code}`);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-pink-50/60 flex flex-col justify-between text-gray-900 selection:bg-pink-100">
      <SEO
        title="Connect with Your Partner on CoupleIn 💕"
        description="Your partner invited you to connect on CoupleIn — the best app for couples to sync daily moods, exchange brownie points, and grow closer together."
        path="/connect"
        keywords="connect partner couplein, pair couple app, partner sync code"
      />

      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between max-w-lg mx-auto w-full">
        <a href="/" className="flex items-center">
          <img src="/couplein_logo.png" alt="CoupleIn" className="h-12 sm:h-14 w-auto object-contain" />
        </a>
        <a
          href="/"
          className="text-xs font-semibold text-gray-500 hover:text-gray-900 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-gray-200 shadow-sm transition"
        >
          About App
        </a>
      </header>

      {/* Main Content Card */}
      <main className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-pink-100/80 text-center relative overflow-hidden">
          {/* Background Ambient Glows */}
          <div className="absolute -top-12 -right-12 w-36 h-36 bg-pink-300/30 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-36 h-36 bg-orange-300/25 rounded-full blur-3xl pointer-events-none" />

          {/* Icon Badge */}
          <div className="relative inline-flex items-center justify-center mb-5">
            <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-[#FF6B9D] via-[#FF8E53] to-[#FFA07A] flex items-center justify-center shadow-lg shadow-pink-500/25 animate-pulse">
              <Heart className="w-10 h-10 text-white fill-white" />
            </div>
            <div className="absolute -top-1 -right-1 bg-amber-400 text-white rounded-full p-1.5 shadow-md">
              <Sparkles className="w-4 h-4 fill-white" />
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
            You&apos;ve Been Invited! 💕
          </h1>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
            Your partner wants to connect with you on <strong className="text-gray-900 font-semibold">CoupleIn</strong> to share moods, exchange brownie points, and strengthen your relationship.
          </p>

          {/* Partner Code Display */}
          {code ? (
            <div className="bg-gradient-to-br from-pink-50/90 to-orange-50/70 border-2 border-dashed border-pink-300/90 rounded-2xl p-4 sm:p-5 mb-6 text-center">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                Partner Connection Code
              </p>
              <div className="flex items-center justify-center gap-1.5 sm:gap-2 mb-3">
                {code.split('').map((char, index) => (
                  <span
                    key={index}
                    className="w-10 h-12 sm:w-11 sm:h-14 bg-white rounded-xl shadow-sm border border-pink-200 flex items-center justify-center text-2xl sm:text-3xl font-bold font-mono text-pink-600"
                  >
                    {char}
                  </span>
                ))}
              </div>

              <Button
                onClick={handleCopyCode}
                variant="outline"
                size="sm"
                className="rounded-full bg-white hover:bg-pink-50 border-pink-200 text-pink-600 font-semibold px-4 shadow-sm transition-all text-xs"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                    <span className="text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                    Copy Code
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="bg-pink-50/70 border border-pink-200 rounded-2xl p-4 mb-6 text-sm text-gray-600">
              <p>Ask your partner for their 6-digit code or open the exact link they sent you.</p>
            </div>
          )}

          {/* Primary Action Button - Open in App */}
          <div className="space-y-3">
            <Button
              onClick={handleOpenApp}
              data-testid="open-couplein-app-btn"
              className="w-full h-13 rounded-2xl bg-gradient-to-r from-[#FF6B9D] via-[#FF758C] to-[#FF8E53] hover:opacity-95 text-white font-bold text-base shadow-lg shadow-pink-500/25 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <Smartphone className="w-5 h-5" />
              Open in CoupleIn App
            </Button>

            {/* Store Download Buttons */}
            <div className="pt-2">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-2.5">
                Don&apos;t have the app installed yet?
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                <a
                  href={IOS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDownloadClick('ios', 'connect_landing')}
                  className="flex items-center justify-center gap-2 py-3 px-3 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-semibold shadow-sm transition active:scale-[0.98]"
                >
                  <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.63-.78 1.06-1.87.94-2.97-1 .04-2.14.65-2.81 1.43-.59.67-1.1 1.77-.96 2.85 1.11.08 2.19-.55 2.83-1.31z"/>
                  </svg>
                  <span>App Store</span>
                </a>
                <a
                  href={ANDROID_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDownloadClick('android', 'connect_landing')}
                  className="flex items-center justify-center gap-2 py-3 px-3 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-semibold shadow-sm transition active:scale-[0.98]"
                >
                  <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24">
                    <path d="M3.609 1.814L13.793 12 3.61 22.186a2.003 2.003 0 0 1-.61-.92L3 2.734c0-.34.12-.66.609-.92zm11.234 11.234l2.58 2.58-12.7 7.23 10.12-9.81zm0-2.096L4.723 1.142l12.7 7.23-2.58 2.58zm1.061 1.048l3.35-1.91a1.503 1.503 0 0 1 0 2.603l-3.35 1.91-1.37-1.301 1.37-1.302z"/>
                  </svg>
                  <span>Google Play</span>
                </a>
              </div>
            </div>

            {/* Continue on Web */}
            <div className="pt-3 border-t border-gray-100">
              <button
                onClick={handleContinueWeb}
                data-testid="continue-web-btn"
                className="text-xs font-medium text-gray-500 hover:text-pink-600 transition inline-flex items-center gap-1"
              >
                Or continue using Web version <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Quick 3-Step Guide */}
          <div className="mt-8 pt-6 border-t border-pink-50 text-left">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
              How to connect in 3 steps:
            </p>
            <ol className="space-y-2 text-xs text-gray-600">
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-pink-100 text-pink-700 font-bold flex items-center justify-center shrink-0">1</span>
                <span>Open or download the <strong>CoupleIn</strong> app.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-pink-100 text-pink-700 font-bold flex items-center justify-center shrink-0">2</span>
                <span>Log in using your Google or Apple account.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-pink-100 text-pink-700 font-bold flex items-center justify-center shrink-0">3</span>
                <span>Enter code <strong className="text-pink-600 font-mono font-bold">{code || '------'}</strong> to connect profiles!</span>
              </li>
            </ol>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-gray-400">
        CoupleIn &copy; {new Date().getFullYear()} &bull;{' '}
        <a href="/privacy" className="hover:underline">Privacy</a> &bull;{' '}
        <a href="/terms" className="hover:underline">Terms</a>
      </footer>
    </div>
  );
}
